import React from 'react';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { getFieldLabel as _fieldLabel } from '@/lib/fieldLabel';

/**
 * ComparisonTableRenderer — renders a period-over-period comparison table
 * with Current, Prior, Variance, and Variance % columns for each measure.
 */
export const ComparisonTableRenderer = React.memo(function ComparisonTableRenderer({ result, vizConfig, formatValue, allFields }) {
  if (!result?.data?.length) return null;

  const cfg = vizConfig || {};
  const { data, dimensions, measures, comparison_columns, current_period, prior_period } = result;
  const cellPad = cfg.compact ? 'px-2 py-0.5' : 'px-3 py-1.5';

  const getFieldLabel = (name) => {
    const f = (allFields || []).find(ff => ff.name === name);
    return _fieldLabel(f, name);
  };

  const currentLabel = String(current_period?.value ?? 'Current');
  const priorLabel = String(prior_period?.value ?? 'Prior');

  return (
    <div className="overflow-auto w-full h-full" data-testid="comparison-table">
      <table className="w-full border-separate text-[11px]" style={{ borderSpacing: 0 }}>
        <thead className="sticky top-0 z-10 bg-white dark:bg-[#0b0b0d]">
          {/* Top header: dimension cols + measure groups */}
          <tr className="bg-muted/40 border-b border-border/50">
            {(dimensions || []).map(d => (
              <th key={d} rowSpan={2} className={`${cellPad} text-[10px] font-semibold text-muted-foreground text-left uppercase tracking-wider bg-muted/40 sticky left-0 z-20`}>
                {getFieldLabel(d)}
              </th>
            ))}
            {(measures || []).map(m => (
              <th key={m} colSpan={4} className={`${cellPad} text-[10px] font-semibold text-foreground text-center border-l-2 border-border/30 bg-muted/30`}>
                {getFieldLabel(m)}
              </th>
            ))}
          </tr>
          {/* Sub-header: Current, Prior, Var, Var% for each measure */}
          <tr className="bg-muted/20 border-b border-border/50">
            {(measures || []).map(m => (
              <React.Fragment key={m}>
                <th className={`${cellPad} text-[9px] font-medium text-primary text-right border-l-2 border-border/20`}>{currentLabel}</th>
                <th className={`${cellPad} text-[9px] font-medium text-muted-foreground text-right`}>{priorLabel}</th>
                <th className={`${cellPad} text-[9px] font-medium text-foreground text-right`}>Variance</th>
                <th className={`${cellPad} text-[9px] font-medium text-foreground text-right`}>Var %</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, ri) => (
            <tr key={ri} className={`border-b border-border/20 hover:bg-muted/20 ${ri % 2 === 1 ? 'bg-muted/5' : ''}`}>
              {(dimensions || []).map(d => (
                <td key={d} className={`${cellPad} font-medium text-foreground sticky left-0 bg-background border-r border-border/30`}>
                  {typeof row[d] === 'object' ? JSON.stringify(row[d]) : String(row[d] ?? '')}
                </td>
              ))}
              {(measures || []).map(m => {
                const cols = comparison_columns?.[m] || {};
                const curr = row[cols.current] ?? 0;
                const prior = row[cols.prior] ?? 0;
                const variance = row[cols.variance] ?? 0;
                const variancePct = row[cols.variance_pct];
                const isPositive = variance > 0;
                const isNegative = variance < 0;
                const isZero = variance === 0;

                return (
                  <React.Fragment key={m}>
                    <td className={`${cellPad} text-right tabular-nums text-primary font-medium border-l-2 border-border/20`}>
                      {formatValue(curr)}
                    </td>
                    <td className={`${cellPad} text-right tabular-nums text-muted-foreground`}>
                      {formatValue(prior)}
                    </td>
                    <td className={`${cellPad} text-right tabular-nums font-medium ${isPositive ? 'text-emerald-400' : isNegative ? 'text-red-400' : 'text-muted-foreground'}`}>
                      <span className="inline-flex items-center gap-0.5">
                        {isPositive && <TrendingUp className="w-3 h-3" />}
                        {isNegative && <TrendingDown className="w-3 h-3" />}
                        {isZero && <Minus className="w-3 h-3" />}
                        {formatValue(Math.abs(variance))}
                      </span>
                    </td>
                    <td className={`${cellPad} text-right tabular-nums ${isPositive ? 'text-emerald-400' : isNegative ? 'text-red-400' : 'text-muted-foreground'}`}>
                      {variancePct !== null && variancePct !== undefined ? (
                        <Badge variant="outline" className={`text-[8px] ${isPositive ? 'border-emerald-500/30 text-emerald-400' : isNegative ? 'border-red-500/30 text-red-400' : 'border-border/30'}`}>
                          {isPositive ? '+' : ''}{variancePct.toFixed(1)}%
                        </Badge>
                      ) : '—'}
                    </td>
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
        {/* Totals */}
        <tfoot>
          <tr className="border-t-2 border-border/50 bg-muted/30 font-bold">
            {(dimensions || []).map((d, i) => (
              <td key={d} className={`${cellPad} text-foreground sticky left-0 bg-muted/30`}>{i === 0 ? 'Total' : ''}</td>
            ))}
            {(measures || []).map(m => {
              const cols = comparison_columns?.[m] || {};
              const totalCurr = data.reduce((s, r) => s + ((r[cols.current] ?? 0) || 0), 0);
              const totalPrior = data.reduce((s, r) => s + ((r[cols.prior] ?? 0) || 0), 0);
              const totalVar = totalCurr - totalPrior;
              const totalPct = totalPrior !== 0 ? (totalVar / totalPrior * 100) : null;
              return (
                <React.Fragment key={m}>
                  <td className={`${cellPad} text-right tabular-nums text-primary font-bold border-l-2 border-border/20`}>{formatValue(totalCurr)}</td>
                  <td className={`${cellPad} text-right tabular-nums text-muted-foreground font-bold`}>{formatValue(totalPrior)}</td>
                  <td className={`${cellPad} text-right tabular-nums font-bold ${totalVar > 0 ? 'text-emerald-400' : totalVar < 0 ? 'text-red-400' : ''}`}>{formatValue(Math.abs(totalVar))}</td>
                  <td className={`${cellPad} text-right tabular-nums ${totalVar > 0 ? 'text-emerald-400' : totalVar < 0 ? 'text-red-400' : ''}`}>
                    {totalPct !== null ? <Badge variant="outline" className={`text-[8px] ${totalVar > 0 ? 'border-emerald-500/30 text-emerald-400' : 'border-red-500/30 text-red-400'}`}>{totalVar > 0 ? '+' : ''}{totalPct.toFixed(1)}%</Badge> : '—'}
                  </td>
                </React.Fragment>
              );
            })}
          </tr>
        </tfoot>
      </table>
      <p className="text-[9px] text-muted-foreground px-3 py-1">{data.length} rows — Comparing {currentLabel} vs {priorLabel}</p>
    </div>
  );
});
